import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandX,
  type Icon as TablerIcon
} from '@tabler/icons-react'

export type SocialNetwork = 'facebook' | 'instagram' | 'linkedin' | 'x'

export type SocialError =
  | 'fb_personal_profile'
  | 'li_personal_profile'
  | 'li_bare_handle'
  | 'invalid_url'
  | 'wrong_host'

export interface NormalizeResult {
  canonical: string
  error: SocialError | null
}

export const SOCIAL_NETWORKS: ReadonlyArray<{
  network: SocialNetwork
  label: string
  Icon: TablerIcon
}> = [
  { network: 'facebook', label: 'Facebook', Icon: IconBrandFacebook },
  { network: 'instagram', label: 'Instagram', Icon: IconBrandInstagram },
  { network: 'linkedin', label: 'LinkedIn', Icon: IconBrandLinkedin },
  { network: 'x', label: 'X (Twitter)', Icon: IconBrandX }
]

const STATIC_MESSAGES: Record<Exclude<SocialError, 'wrong_host'>, string> = {
  fb_personal_profile:
    'Please use your Facebook Page URL (e.g. facebook.com/YourPreschoolName), not a personal profile link.',
  li_personal_profile: "Please use your organization's LinkedIn page, not a personal profile.",
  li_bare_handle: 'Please paste your full LinkedIn URL.',
  invalid_url: 'Please enter a valid handle or URL.'
}

const WRONG_HOST_MESSAGES: Record<SocialNetwork, string> = {
  facebook: "This URL doesn't look like a Facebook link.",
  instagram: "This URL doesn't look like an Instagram link.",
  linkedin: "This URL doesn't look like a LinkedIn link.",
  x: "This URL doesn't look like an X (Twitter) link."
}

export const getSocialErrorMessage = (network: SocialNetwork, error: SocialError): string => {
  if (error === 'wrong_host') {
    return WRONG_HOST_MESSAGES[network]
  }
  return STATIC_MESSAGES[error]
}

const stripTrailingSlash = (s: string) => s.replace(/\/+$/, '')
const stripLeadingAt = (s: string) => s.replace(/^@/, '')

const tryParseUrl = (raw: string): URL | null => {
  const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
  try {
    return new URL(candidate)
  } catch {
    return null
  }
}

// Used by LinkedIn to detect a bareword-shaped input and reject it as a bare handle.
const isPlainHandle = (s: string) => /^[A-Za-z0-9._-]+$/.test(s)

// Per-network handle rules (approximate the platforms' published constraints).
const isFacebookHandle = (s: string) => /^[A-Za-z0-9.-]{1,50}$/.test(s)
const isInstagramHandle = (s: string) => /^[A-Za-z0-9._]{1,30}$/.test(s)
const isXHandle = (s: string) => /^[A-Za-z0-9_]{1,15}$/.test(s)

const HOSTS: Record<SocialNetwork, string[]> = {
  facebook: ['facebook.com', 'www.facebook.com', 'm.facebook.com'],
  instagram: ['instagram.com', 'www.instagram.com'],
  linkedin: ['linkedin.com', 'www.linkedin.com'],
  x: ['x.com', 'www.x.com', 'twitter.com', 'www.twitter.com', 'mobile.twitter.com']
}

const hostMatches = (network: SocialNetwork, hostname: string) =>
  HOSTS[network].includes(hostname.toLowerCase())

const normalizeFacebook = (raw: string): NormalizeResult => {
  const stripped = stripLeadingAt(raw.replace(/\?.*$/, '').replace(/#.*$/, ''))
  if (stripped.toLowerCase() === 'profile.php') {
    return { canonical: '', error: 'fb_personal_profile' }
  }
  if (isFacebookHandle(stripped)) {
    return { canonical: `https://www.facebook.com/${stripped}`, error: null }
  }
  const url = tryParseUrl(raw)
  if (!url) return { canonical: '', error: 'invalid_url' }
  if (!hostMatches('facebook', url.hostname)) return { canonical: '', error: 'wrong_host' }
  if (url.pathname.toLowerCase().startsWith('/profile.php') || url.searchParams.has('id')) {
    return { canonical: '', error: 'fb_personal_profile' }
  }
  const handle = stripTrailingSlash(url.pathname).slice(1)
  if (!handle || !isFacebookHandle(handle)) return { canonical: '', error: 'invalid_url' }
  return { canonical: `https://www.facebook.com/${handle}`, error: null }
}

const normalizeInstagram = (raw: string): NormalizeResult => {
  const stripped = stripLeadingAt(raw.replace(/\?.*$/, '').replace(/#.*$/, ''))
  if (isInstagramHandle(stripped)) {
    return { canonical: `https://www.instagram.com/${stripped}`, error: null }
  }
  const url = tryParseUrl(raw)
  if (!url) return { canonical: '', error: 'invalid_url' }
  if (!hostMatches('instagram', url.hostname)) return { canonical: '', error: 'wrong_host' }
  const handle = stripTrailingSlash(url.pathname).slice(1)
  if (!handle || !isInstagramHandle(handle)) return { canonical: '', error: 'invalid_url' }
  return { canonical: `https://www.instagram.com/${handle}`, error: null }
}

const normalizeLinkedin = (raw: string): NormalizeResult => {
  if (isPlainHandle(stripLeadingAt(raw))) {
    return { canonical: '', error: 'li_bare_handle' }
  }
  const url = tryParseUrl(raw)
  if (!url) return { canonical: '', error: 'invalid_url' }
  if (!hostMatches('linkedin', url.hostname)) return { canonical: '', error: 'wrong_host' }
  const match = stripTrailingSlash(url.pathname).match(/^\/(company|school)\/([A-Za-z0-9._-]+)$/i)
  if (!match) {
    if (/^\/in\//i.test(url.pathname)) return { canonical: '', error: 'li_personal_profile' }
    return { canonical: '', error: 'li_bare_handle' }
  }
  const [, kind, slug] = match
  return { canonical: `https://www.linkedin.com/${kind.toLowerCase()}/${slug}`, error: null }
}

const normalizeX = (raw: string): NormalizeResult => {
  const stripped = stripLeadingAt(raw.replace(/\?.*$/, '').replace(/#.*$/, ''))
  if (isXHandle(stripped)) {
    return { canonical: `https://x.com/${stripped}`, error: null }
  }
  const url = tryParseUrl(raw)
  if (!url) return { canonical: '', error: 'invalid_url' }
  if (!hostMatches('x', url.hostname)) return { canonical: '', error: 'wrong_host' }
  const handle = stripTrailingSlash(url.pathname).slice(1)
  if (!handle || !isXHandle(handle)) return { canonical: '', error: 'invalid_url' }
  return { canonical: `https://x.com/${handle}`, error: null }
}

export const normalizeSocialUrl = (network: SocialNetwork, raw: string): NormalizeResult => {
  const trimmed = (raw ?? '').trim()
  if (trimmed === '') return { canonical: '', error: null }
  switch (network) {
    case 'facebook':
      return normalizeFacebook(trimmed)
    case 'instagram':
      return normalizeInstagram(trimmed)
    case 'linkedin':
      return normalizeLinkedin(trimmed)
    case 'x':
      return normalizeX(trimmed)
  }
}
